import { Injectable, inject } from '@angular/core';
import { NotificationRef, NotificationService } from '@progress/kendo-angular-notification';
import { WINDOW } from '../../../features/calendar/util/browser-window-ref';
import { NotificationComponent } from '../../../shared/components/notification/notification.component';
import { ShiveRenderService } from '../rendering/shive-render.service';

@Injectable({
  providedIn: 'root',
})
export class ShiveNotificationService {
  private notificationRef: NotificationRef;
  private hideTimeoutId: number | null = null;

  // DI
  private readonly notificationService = inject(NotificationService);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly window = inject<Window>(WINDOW);

  public show(notificationType: NotificationType = NotificationType.None, content?: string): void {
    // Close any existing one via the public API
    this.notificationRef?.hide();

    this.showNotification(content, notificationType);
  }

  public hide(): void {
    if (!this.notificationRef) {
      return;
    }

    const parent = this.notificationRef.notification.location.nativeElement as HTMLElement;
    const element = parent.querySelector('.k-notification');

    if (!element) {
      this.notificationRef.hide();
      return;
    }

    // Trigger fadeout
    element.classList.add('closing');

    const onAnimationEnd = () => {
      element.removeEventListener('animationend', onAnimationEnd);
      this.notificationRef.hide();
    };

    element.addEventListener('animationend', onAnimationEnd);
  }

  private showNotification(content: string, notificationType: NotificationType): void {
    this.notificationRef = this.notificationService.show({
      content: NotificationComponent,
      cssClass: 'button-notification',
      position: { horizontal: 'center', vertical: 'bottom' },
      type: { style: notificationType, icon: false },
      closable: true,
    });

    const notificationComponent = this.notificationRef.content.instance as NotificationComponent;
    notificationComponent.content = content;
    notificationComponent.type = notificationType;
    notificationComponent.notificationRef = this.notificationRef;

    // clear previous timer
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
    }

    const visibleNotificationDuration = this.shiveRenderService.getCSSVar(
      '--visible-notification-duration',
    );

    this.hideTimeoutId = this.window.setTimeout(() => {
      this.hide();
    }, +visibleNotificationDuration + 100);
  }
}

export enum NotificationType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
  None = 'none',
}
